import React from "react";
import { NPIcon } from "./atoms";
import type { ContextMeshCategoryKey } from "../meshStrength/contextMeshAnalysis";
import { CONTEXT_MESH_CATEGORY_THEME } from "../meshStrength/contextMeshTheme";
import type { CanonicalMeshCardAnalysis } from "./MeshCard";

const QUADRANT_COPY: Record<ContextMeshCategoryKey, { what: string; why: string }> = {
    discover: { what: "What the product is, the problem it solves, and who it serves.", why: "Without it the AI guesses at intent — requirements and stories drift off target." },
    external: { what: "Jira or Azure DevOps — your live backlog, sprints, and work items.", why: "Grounds every plan in what your team actually tracks instead of assumptions." },
    file: { what: "Specs, personas, and design docs you upload.", why: "Your own documents become ground truth the AI can quote, not guess at." },
    code: { what: "Git repos — real code structure, commits, and pull requests.", why: "Generated code fits how your system is actually built." }
};

const QUADRANTS = (["discover", "external", "file", "code"] as const).map(key => ({
    ...CONTEXT_MESH_CATEGORY_THEME[key],
    sub: CONTEXT_MESH_CATEGORY_THEME[key].subtitle,
    ...QUADRANT_COPY[key]
}));

function QuadRow({ quadrant, analysis }: { quadrant: (typeof QUADRANTS)[number]; analysis: CanonicalMeshCardAnalysis }) {
    const category = analysis.categoryScores[quadrant.key];
    return (
        <div style={{ display: "flex", gap: 13, padding: "13px 15px", borderRadius: 13, border: "1px solid var(--divider)", background: "var(--bg-paper)", marginBottom: 10 }}>
            <span style={{ width: 12, height: 12, borderRadius: 7, background: quadrant.color, flex: "0 0 auto", marginTop: 3 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 3 }}>
                    <span style={{ fontSize: 13.5, fontWeight: 650 }}>{quadrant.title}</span>
                    <span style={{ fontSize: 11, color: "var(--fg4)" }}>{quadrant.sub}</span>
                    <span style={{ flex: 1 }} />
                    <span style={{ fontSize: 11, fontWeight: 700, color: quadrant.accentColor }}>{category.score}% · {category.band}</span>
                </div>
                <div style={{ fontSize: 12, color: "var(--fg2)", lineHeight: 1.5 }}>{quadrant.what}</div>
                <div style={{ fontSize: 11.5, color: "var(--fg4)", lineHeight: 1.5, marginTop: 2 }}><b style={{ color: "var(--fg3)" }}>Why it matters:</b> {quadrant.why}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 7 }}>
                    <div style={{ flex: 1, height: 5, borderRadius: 5, background: "var(--bg-input)", overflow: "hidden" }}>
                        <div style={{ width: `${category.score}%`, height: "100%", borderRadius: 5, background: quadrant.color }} />
                    </div>
                    <span style={{ fontSize: 10.5, color: "var(--fg3)", fontWeight: 600 }}>{category.summary}</span>
                </div>
            </div>
        </div>
    );
}

export function MeshInspector({ open, onClose, analysis, updating }: { open: boolean; onClose: () => void; analysis: CanonicalMeshCardAnalysis | null; updating: boolean }) {
    return (
        <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, right: 239, zIndex: 5, overflow: "hidden", pointerEvents: open ? "auto" : "none", borderRadius: "18px 0 0 18px" }}>
            <div style={{ position: "absolute", inset: 0, background: "var(--bg-paper)", border: "1.5px solid var(--np-brand)", borderRight: "none", borderRadius: "18px 0 0 18px", boxShadow: "-14px 0 40px rgba(15,23,42,0.16)", overflow: "hidden auto", padding: "16px 22px", transform: open ? "translateX(0)" : "translateX(102%)", opacity: open ? 1 : 0, transition: "transform .38s cubic-bezier(.22,.8,.3,1), opacity .25s" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <span style={{ fontSize: 16, fontWeight: 650 }}>Your Context Mesh</span>
                    {updating && <span style={{ fontSize: 11, fontWeight: 700, color: "var(--np-brand)" }}>Updating…</span>}
                    <span style={{ flex: 1 }} />
                    <button type="button" onClick={onClose} title="Close" style={{ width: 28, height: 28, borderRadius: 9, border: "1px solid var(--divider)", background: "transparent", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><NPIcon name="x" size={13} sw={2.2} color="var(--fg3)" /></button>
                </div>
                <div
                    role="note"
                    style={{
                        fontSize: 12,
                        lineHeight: 1.5,
                        color: "var(--fg3)",
                        marginBottom: 12,
                        padding: "9px 11px",
                        borderRadius: 10,
                        border: "1px solid rgba(16, 54, 153,0.18)",
                        background: "rgba(16, 54, 153,0.05)"
                    }}
                >
                    <strong style={{ color: "var(--fg2)", display: "block", marginBottom: 2 }}>Scores evolve as you add context.</strong>
                    <span>All scores shown here reflect the context available now. The overall score may change after the product is created or as other context is added.</span>
                </div>
                {analysis && (
                    <>
                        {QUADRANTS.map(quadrant => <QuadRow key={quadrant.key} quadrant={quadrant} analysis={analysis} />)}
                        <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 15px", borderRadius: 13, background: "rgba(16, 54, 153,0.05)", border: "1px solid rgba(16, 54, 153,0.18)", marginTop: 2 }}>
                            <div style={{ flex: "0 0 auto", textAlign: "center" }}>
                                <div style={{ fontFamily: "var(--font-mono)", fontSize: 24, fontWeight: 700, color: "var(--np-brand)", lineHeight: 1 }}>{analysis.score}</div>
                                <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--np-brand)" }}>{analysis.tier}</div>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ height: 7, borderRadius: 7, background: "var(--bg-input)", overflow: "hidden", marginBottom: 6 }}><div style={{ width: `${analysis.score}%`, height: "100%", borderRadius: 7, background: "var(--np-brand)" }} /></div>
                                <div style={{ fontSize: 11.5, color: "var(--fg2)", lineHeight: 1.5 }}>Readiness {analysis.readinessScore}% · Quality {analysis.qualityScore}%</div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
